<script setup>
/**
 * DepartmentsMenu Component
 * Two-level navigation: Departments → SubDepartments
 * Supports search across both levels with grouped results
 */
import { ref, computed, watch, toRef } from 'vue'
import { useBackButtonClose } from '../../../core/composables/useBackButtonClose.js'

// ============================================
// Props
// ============================================
const props = defineProps({
  departments: {
    type: Array,
    default: () => [],
  },
  subDepartments: {
    type: Array,
    default: () => [],
  },
  subsByDepartmentId: {
    type: Object,
    default: () => ({}),
  },
  catalogItems: {
    type: Array,
    default: () => [],
  },
  selectedDepartmentId: {
    type: String,
    default: null,
  },
  selectedDepartmentName: {
    type: String,
    default: null,
  },
  selectedSubDepartmentId: {
    type: String,
    default: null,
  },
  selectedSubDepartmentName: {
    type: String,
    default: null,
  },
  viewMode: {
    type: String,
    default: 'departments', // 'departments' | 'subs'
  },
  isMobileOpen: {
    type: Boolean,
    default: false,
  },
  isDesktopCollapsed: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  subsLoading: {
    type: Boolean,
    default: false,
  },
  showTutorial: {
    type: Boolean,
    default: false,
  },
})

// ============================================
// Emits
// ============================================
const emit = defineEmits([
  'select-department',
  'select-subdepartment',
  'go-back',
  'toggle-mobile',
  'toggle-desktop',
  'update-search',
  'request-subs-for-search',
])

// Intercept back button to close mobile overlay instead of navigating
useBackButtonClose(toRef(props, 'isMobileOpen'), () => emit('toggle-mobile'))

// ============================================
// Local State
// ============================================
const searchQuery = ref('')
const searchSubsRequested = ref(false)

// ============================================
// Computed - Search Results
// ============================================
const isSearchActive = computed(() => searchQuery.value.trim().length > 0)

const searchResults = computed(() => {
  if (!isSearchActive.value) return { departments: [], subs: [] }

  const query = searchQuery.value.trim().toLowerCase()

  // Search departments
  const matchingDepts = props.departments.filter((dept) => dept.name.toLowerCase().includes(query))

  // Search all subs from cache
  const matchingSubs = []
  for (const [deptId, subs] of Object.entries(props.subsByDepartmentId)) {
    const dept = props.departments.find((d) => d.id === deptId)
    for (const sub of subs) {
      if (sub.name.toLowerCase().includes(query)) {
        matchingSubs.push({
          ...sub,
          parentDepartmentId: deptId,
          parentDepartmentName: dept?.name || '',
        })
      }
    }
  }

  return {
    departments: matchingDepts,
    subs: matchingSubs,
  }
})

const hasSearchResults = computed(() => {
  return searchResults.value.departments.length > 0 || searchResults.value.subs.length > 0
})

// Filtered departments (for non-search departments view)
const filteredDepartments = computed(() => {
  if (isSearchActive.value) return []
  return props.departments
})

// ============================================
// Methods
// ============================================
function selectDepartment(dept) {
  clearSearch()
  emit('select-department', { id: dept.id, name: dept.name })
}

function selectSubDepartment(sub) {
  clearSearch()
  emit('select-subdepartment', {
    id: sub.id,
    name: sub.name,
    departmentId: sub.parentDepartmentId,
    departmentName: sub.parentDepartmentName,
  })
}

function goBack() {
  // If search is active, clear it first
  if (isSearchActive.value) {
    clearSearch()
    return
  }
  // Otherwise go back to departments view
  emit('go-back')
}

function toggleMobile() {
  emit('toggle-mobile')
}

function toggleDesktop() {
  emit('toggle-desktop')
}

function handleSearch(event) {
  const query = event.target.value
  searchQuery.value = query
  emit('update-search', query)

  // Request subs for search when user starts typing
  if (query.trim() && !searchSubsRequested.value) {
    searchSubsRequested.value = true
    emit('request-subs-for-search')
  }
}

function clearSearch() {
  searchQuery.value = ''
  emit('update-search', '')
}

// Determine header title based on view
const headerTitle = computed(() => {
  if (isSearchActive.value) return 'חיפוש'
  if (props.viewMode === 'subs' && props.selectedDepartmentName) {
    return props.selectedDepartmentName
  }
  return 'מחלקות'
})

// Show back button?
const showBackButton = computed(() => {
  return isSearchActive.value || props.viewMode === 'subs'
})

// Reset search when mobile closes
watch(
  () => props.isMobileOpen,
  (isOpen) => {
    if (!isOpen) {
      clearSearch()
    }
  }
)

// Get first item image for a subdepartment
function getSubPreviewImage(subId) {
  const item = props.catalogItems.find(
    (item) => item.subDepartment === subId && item.image && item.image.trim()
  )
  return item?.image || null
}
</script>

<template>
  <!-- Mobile Overlay -->
  <Teleport to="body">
    <Transition name="overlay">
      <div v-if="isMobileOpen" class="mobile-overlay">
        <div class="overlay-backdrop" @click="toggleMobile"></div>
        <div class="overlay-content">
          <!-- Overlay Header -->
          <header class="overlay-header">
            <button class="close-btn" @click="toggleMobile">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <h2 class="overlay-title">{{ headerTitle }}</h2>
            <div class="header-spacer"></div>
          </header>

          <!-- Back Button (mobile) -->
          <div v-if="showBackButton" class="back-container">
            <button class="back-btn active" @click="goBack">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M5 12H19M12 5l7 7-7 7" />
              </svg>
              <span>חזרה למחלקות</span>
            </button>
          </div>

          <!-- Search -->
          <div class="search-container">
            <div class="search-input-wrapper">
              <svg
                class="search-icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                class="search-input"
                placeholder="חיפוש מחלקה או תת־מחלקה..."
                :value="searchQuery"
                @input="handleSearch"
              />
              <button v-if="searchQuery" class="search-clear" @click="clearSearch">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Content Area -->
          <div class="menu-content">
            <!-- Search Results -->
            <template v-if="isSearchActive">
              <div v-if="!hasSearchResults" class="list-empty">
                <p>לא נמצאו תוצאות</p>
              </div>
              <template v-else>
                <!-- Departments Group -->
                <div v-if="searchResults.departments.length > 0" class="search-group">
                  <div class="search-group-header">מחלקות</div>
                  <button
                    v-for="dept in searchResults.departments"
                    :key="dept.id"
                    class="menu-item"
                    :class="{ selected: dept.id === selectedDepartmentId }"
                    @click="selectDepartment(dept)"
                  >
                    <span class="item-name">{{ dept.name }}</span>
                    <svg
                      class="chevron-icon"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                </div>

                <!-- SubDepartments Group -->
                <div v-if="searchResults.subs.length > 0" class="search-group">
                  <div class="search-group-header">תתי־מחלקות</div>
                  <button
                    v-for="sub in searchResults.subs"
                    :key="sub.id"
                    class="menu-item sub-item"
                    :class="{ selected: sub.id === selectedSubDepartmentId }"
                    @click="selectSubDepartment(sub)"
                  >
                    <div class="item-content">
                      <span class="item-name">{{ sub.name }}</span>
                      <span class="item-parent">{{ sub.parentDepartmentName }}</span>
                    </div>
                    <svg
                      v-if="sub.id === selectedSubDepartmentId"
                      class="check-icon"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </button>
                </div>
              </template>
            </template>

            <!-- Departments List -->
            <template v-else-if="viewMode === 'departments'">
              <div v-if="isLoading" class="list-loading">
                <div class="spinner"></div>
                <span>טוען מחלקות...</span>
              </div>

              <div v-else-if="departments.length === 0" class="list-empty">
                <p>אין מחלקות להצגה</p>
              </div>

              <template v-else>
                <button
                  v-for="dept in departments"
                  :key="dept.id"
                  class="menu-item"
                  :class="{
                    selected: dept.id === selectedDepartmentId,
                    'dept-promo': dept.isPromoDept,
                  }"
                  @click="selectDepartment(dept)"
                >
                  <span class="item-name">
                    <!-- Star icon for promo department -->
                    <svg
                      v-if="dept.isPromoDept"
                      class="promo-star-icon"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="currentColor"
                    >
                      <path
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      />
                    </svg>
                    {{ dept.name }}
                  </span>
                  <!-- Hide chevron for promo department (no subs) -->
                  <svg
                    v-if="!dept.isPromoDept"
                    class="chevron-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              </template>
            </template>

            <!-- SubDepartments List -->
            <template v-else-if="viewMode === 'subs'">
              <div v-if="subsLoading" class="list-loading">
                <div class="spinner"></div>
                <span>טוען תתי־מחלקות...</span>
              </div>

              <div v-else-if="subDepartments.length === 0" class="list-empty">
                <p>אין תתי־מחלקות במחלקה זו</p>
              </div>

              <template v-else>
                <!-- Prompt to select -->
                <div v-if="!selectedSubDepartmentId" class="select-prompt">בחר תת־מחלקה</div>
                <button
                  v-for="sub in subDepartments"
                  :key="sub.id"
                  class="menu-item sub-with-image"
                  :class="{ selected: sub.id === selectedSubDepartmentId }"
                  @click="
                    selectSubDepartment({
                      ...sub,
                      parentDepartmentId: selectedDepartmentId,
                      parentDepartmentName: selectedDepartmentName,
                    })
                  "
                >
                  <span class="item-name">{{ sub.name }}</span>
                  <div class="sub-preview">
                    <svg
                      v-if="sub.id === selectedSubDepartmentId"
                      class="check-icon"
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <img
                      v-if="getSubPreviewImage(sub.id)"
                      :src="getSubPreviewImage(sub.id)"
                      :alt="sub.name"
                      class="sub-preview-img"
                    />
                    <div v-else class="sub-preview-placeholder">
                      <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                    </div>
                  </div>
                </button>
              </template>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Desktop Sidebar -->
  <aside
    v-tutorial="
      showTutorial
        ? {
            id: 'catalog-desktop-departments',
            text: 'כאן בוחרים מחלקה ותת־מחלקה — כל המוצרים מסודרים לפי קטגוריות',
            importance: 'important',
            showOnce: true,
            autoTrigger: false,
            group: 'catalog-desktop-intro',
            step: 3,
          }
        : undefined
    "
    class="desktop-sidebar"
    :class="{ collapsed: isDesktopCollapsed }"
  >
    <!-- Menu Content (only shown when expanded) -->
    <div v-if="!isDesktopCollapsed" class="sidebar-expanded">
      <!-- Header Title -->
      <header class="sidebar-header">
        <h3 class="sidebar-title">{{ headerTitle }}</h3>
      </header>

      <!-- Back Button -->
      <div class="back-container">
        <button
          class="back-btn"
          :class="{ active: showBackButton }"
          :disabled="!showBackButton"
          @click="goBack"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M5 12H19M12 5l7 7-7 7" />
          </svg>
          <span>חזרה למחלקות</span>
        </button>
      </div>

      <!-- Search -->
      <div class="search-container">
        <div class="search-input-wrapper">
          <svg
            class="search-icon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            class="search-input"
            placeholder="חיפוש..."
            :value="searchQuery"
            @input="handleSearch"
          />
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="menu-content">
        <!-- Search Results -->
        <template v-if="isSearchActive">
          <div v-if="!hasSearchResults" class="list-empty">
            <p>לא נמצאו תוצאות</p>
          </div>
          <template v-else>
            <!-- Departments Group -->
            <div v-if="searchResults.departments.length > 0" class="search-group">
              <div class="search-group-header">מחלקות</div>
              <button
                v-for="dept in searchResults.departments"
                :key="dept.id"
                class="menu-item"
                :class="{ selected: dept.id === selectedDepartmentId }"
                @click="selectDepartment(dept)"
              >
                <span class="item-name">{{ dept.name }}</span>
                <svg
                  class="chevron-icon"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            </div>

            <!-- SubDepartments Group -->
            <div v-if="searchResults.subs.length > 0" class="search-group">
              <div class="search-group-header">תתי־מחלקות</div>
              <button
                v-for="sub in searchResults.subs"
                :key="sub.id"
                class="menu-item sub-item"
                :class="{ selected: sub.id === selectedSubDepartmentId }"
                @click="selectSubDepartment(sub)"
              >
                <div class="item-content">
                  <span class="item-name">{{ sub.name }}</span>
                  <span class="item-parent">{{ sub.parentDepartmentName }}</span>
                </div>
                <svg
                  v-if="sub.id === selectedSubDepartmentId"
                  class="check-icon"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </button>
            </div>
          </template>
        </template>

        <!-- Departments List -->
        <template v-else-if="viewMode === 'departments'">
          <div v-if="isLoading" class="list-loading">
            <div class="spinner"></div>
            <span>טוען...</span>
          </div>

          <div v-else-if="departments.length === 0" class="list-empty">
            <p>אין מחלקות</p>
          </div>

          <template v-else>
            <button
              v-for="dept in departments"
              :key="dept.id"
              class="menu-item"
              :class="{
                selected: dept.id === selectedDepartmentId,
                'dept-promo': dept.isPromoDept,
              }"
              @click="selectDepartment(dept)"
            >
              <span class="item-name">
                <!-- Star icon for promo department -->
                <svg
                  v-if="dept.isPromoDept"
                  class="promo-star-icon"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="currentColor"
                >
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  />
                </svg>
                {{ dept.name }}
              </span>
              <!-- Hide chevron for promo department (no subs) -->
              <svg
                v-if="!dept.isPromoDept"
                class="chevron-icon"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          </template>
        </template>

        <!-- SubDepartments List -->
        <template v-else-if="viewMode === 'subs'">
          <div v-if="subsLoading" class="list-loading">
            <div class="spinner"></div>
            <span>טוען...</span>
          </div>

          <div v-else-if="subDepartments.length === 0" class="list-empty">
            <p>אין תתי־מחלקות</p>
          </div>

          <template v-else>
            <!-- Prompt to select -->
            <div v-if="!selectedSubDepartmentId" class="select-prompt">בחר תת־מחלקה</div>
            <button
              v-for="sub in subDepartments"
              :key="sub.id"
              class="menu-item sub-with-image"
              :class="{ selected: sub.id === selectedSubDepartmentId }"
              @click="
                selectSubDepartment({
                  ...sub,
                  parentDepartmentId: selectedDepartmentId,
                  parentDepartmentName: selectedDepartmentName,
                })
              "
            >
              <span class="item-name">{{ sub.name }}</span>
              <div class="sub-preview">
                <svg
                  v-if="sub.id === selectedSubDepartmentId"
                  class="check-icon"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <img
                  v-if="getSubPreviewImage(sub.id)"
                  :src="getSubPreviewImage(sub.id)"
                  :alt="sub.name"
                  class="sub-preview-img"
                />
                <div v-else class="sub-preview-placeholder">
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                </div>
              </div>
            </button>
          </template>
        </template>
      </div>
    </div>
  </aside>
</template>

<style scoped>
/* ============================================
   Mobile Overlay
   ============================================ */
.mobile-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: none;
}

/* Show mobile overlay for both mobile and tablet (< 1280px) */
@media (max-width: 1279px) {
  .mobile-overlay {
    display: block;
  }
}

.overlay-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.overlay-content {
  position: absolute;
  inset: 0;
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
}

.overlay-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-primary);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.close-btn:hover {
  background: var(--bg-tertiary);
}

.overlay-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.header-spacer {
  width: 40px;
}

/* ============================================
   Desktop Sidebar
   ============================================ */
.desktop-sidebar {
  display: none;
  flex: 1;
  min-height: 0;
  background: var(--bg-primary);
  border-left: 1px solid var(--border-color);
  transition: width var(--transition-normal);
  overflow: hidden;
}

@media (min-width: 768px) {
  .desktop-sidebar {
    display: flex;
    width: 100%;
    border-left: none;
  }

  .desktop-sidebar.collapsed {
    width: 48px;
  }
}

.sidebar-expanded {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.sidebar-header {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.sidebar-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ============================================
   Back Button
   ============================================ */
.back-container {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color-light);
  display: flex;
  justify-content: center;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: 10px var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  color: var(--text-muted);
  cursor: not-allowed;
  opacity: 0.5;
  transition: all var(--transition-fast);
}

.back-btn.active {
  color: var(--color-primary);
  background: rgba(40, 53, 72, 0.06);
  border-color: var(--color-primary);
  cursor: pointer;
  opacity: 1;
}

.back-btn.active:hover {
  background: rgba(40, 53, 72, 0.12);
}

/* ============================================
   Search Container
   ============================================ */
.search-container {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color-light);
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  right: 10px;
  color: var(--text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  padding-right: 36px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-secondary);
  color: var(--text-primary);
  transition: border-color var(--transition-fast);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  background: var(--bg-primary);
}

.search-input::placeholder {
  color: var(--text-muted);
}

.search-clear {
  position: absolute;
  left: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius-full);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.search-clear:hover {
  background: var(--color-gray-300);
  color: var(--text-primary);
}

/* ============================================
   Menu Content
   ============================================ */
.menu-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-xs);
}

/* Premium Minimalist Scrollbar */
.menu-content::-webkit-scrollbar {
  width: 6px;
}

.menu-content::-webkit-scrollbar-track {
  background: transparent;
}

.menu-content::-webkit-scrollbar-thumb {
  background: var(--border-color);
}

.menu-content::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

.list-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  gap: var(--spacing-sm);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-primary-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  text-align: center;
}

.list-empty p {
  margin: 0;
}

.select-prompt {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  text-align: center;
  border-bottom: 1px solid var(--border-color-light);
  margin-bottom: var(--spacing-xs);
}

/* ============================================
   Search Groups
   ============================================ */
.search-group {
  margin-bottom: var(--spacing-sm);
}

.search-group-header {
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--bg-secondary);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-xs);
}

/* ============================================
   Menu Items
   ============================================ */
.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  text-align: right;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.menu-item:hover {
  background: var(--bg-secondary);
}

.menu-item.selected {
  background: rgba(99, 102, 241, 0.1);
  color: var(--color-primary);
  font-weight: var(--font-weight-medium);
}

.menu-item.sub-item {
  flex-direction: row;
}

.item-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
}

.item-name {
  flex: 1;
  text-align: right;
}

.item-parent {
  font-size: 11px;
  color: var(--text-muted);
}

.chevron-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

.check-icon {
  color: var(--color-primary);
  flex-shrink: 0;
}

/* ============================================
   Sub-department Preview Image
   ============================================ */
.menu-item.sub-with-image {
  align-items: center;
}

.sub-preview {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
}

.sub-preview-img,
.sub-preview-placeholder {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color-light);
}

.sub-preview-img {
  object-fit: contain;
  background: #ffffff;
}

.sub-preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  opacity: 0.5;
}

/* ============================================
   Overlay Transitions
   ============================================ */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-enter-active .overlay-content,
.overlay-leave-active .overlay-content {
  transition: transform 0.25s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-from .overlay-content {
  transform: translateX(100%);
}

.overlay-leave-to .overlay-content {
  transform: translateX(100%);
}

/* ============================================
   Mobile-specific Styles
   ============================================ */
@media (max-width: 767px) {
  .menu-item {
    padding: var(--spacing-md);
    font-size: var(--font-size-base);
    border-bottom: 1px solid var(--border-color-light);
    border-radius: 0;
  }

  .menu-item:last-child {
    border-bottom: none;
  }

  .menu-item:active {
    background: var(--bg-tertiary);
  }

  .menu-content {
    padding: 0;
  }

  .search-input {
    padding: 12px 16px;
    padding-right: 40px;
    font-size: var(--font-size-base);
  }

  .search-group-header {
    border-radius: 0;
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .select-prompt {
    padding: var(--spacing-md);
    font-size: var(--font-size-sm);
  }

  .back-container {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .back-btn {
    font-size: var(--font-size-base);
    padding: 12px var(--spacing-md);
  }

  .back-btn.active {
    color: var(--color-info);
    border-color: var(--color-info);
    background: rgba(59, 130, 246, 0.08);
  }

  .back-btn.active:hover {
    background: rgba(59, 130, 246, 0.15);
  }
}

/* ============================================
   Promo Department Styling
   ============================================ */
.menu-item.dept-promo {
  /* No special border on desktop */
}

.menu-item.dept-promo .item-name {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dc2626;
  font-weight: var(--font-weight-semibold);
}

.menu-item.dept-promo .promo-star-icon {
  flex-shrink: 0;
  color: #dc2626;
}

.menu-item.dept-promo:hover {
  background: var(--bg-secondary);
}

.menu-item.dept-promo.selected {
  background: rgba(220, 38, 38, 0.08);
}

.menu-item.dept-promo.selected .item-name {
  color: #b91c1c;
}

.menu-item.dept-promo.selected .promo-star-icon {
  color: #b91c1c;
}

@media (max-width: 767px) {
  .menu-item.dept-promo {
    border-right: 4px solid #dc2626;
  }

  .menu-item.dept-promo.selected {
    border-right-color: #b91c1c;
  }
}
</style>
